<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="d-flex justify-space-between mb-6 align-center">
      <p class="h6 pa-0 mb-0">
        {{ $t('organization_settings.admin_settings') }}
      </p>

      <OrganizationMenu :organizationId="organization._id" />
    </div>

    <v-form v-model="formOptions.valid" ref="settings">
      <v-text-field
        v-model="organization.sitesAvailable"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-sitesAvailable"
        dense
        outlined
        required
        :rules="[
          sitesAvailable =>
            !!sitesAvailable || $t('validation.form.this_field_is_required'),
          sitesAvailable =>
            sitesAvailable <= maxSitesAvailable ||
            $t('organization_settings.sites_max', {
              value: maxSitesAvailable.toLocaleString(language),
            }),
        ]"
        :label="`${$t('organization.sites_available')}*`"
      ></v-text-field>

      <v-text-field
        v-model="organization.licenses"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-licenses"
        dense
        outlined
        required
        :rules="[
          licences =>
            !!licences || $t('validation.form.this_field_is_required'),
          licences =>
            licences <= maxLicenses ||
            $t('organization_settings.licenses_max', {
              value: maxLicenses.toLocaleString(language),
            }),
        ]"
        :label="`${$t('common.license_other')}*`"
      ></v-text-field>

      <v-select
        v-model="organization.type"
        class="mb-2"
        data-testid="adminSettings-select-type"
        dense
        item-text="name"
        outlined
        required
        :rules="[
          type => !!type || $t('validation.form.this_field_is_required'),
        ]"
        :label="`${$t('common.type')}*`"
        :items="organizationTypes"
      ></v-select>

      <v-select
        v-model="organization.mediaProvider"
        class="mb-2"
        data-testid="adminSettings-select-media_provider"
        dense
        item-text="name"
        outlined
        required
        :rules="[
          mediaProvider =>
            !!mediaProvider || $t('validation.form.this_field_is_required'),
        ]"
        :label="`${$t('organization.media_provider')}*`"
        :items="mediaProviders"
      ></v-select>
      <v-text-field
        v-if="organization.mediaProvider === 'EXTERNAL_AZURE_STORAGE_SERVICE'"
        v-model="organization.providers__azure_storage_storageURL"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-media-url"
        dense
        outlined
        :required="
          organization.mediaProvider === 'EXTERNAL_AZURE_STORAGE_SERVICE'
        "
        :label="`${$t('organization.providers__azure_storage_storageURL')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-text-field>

      <v-select
        v-model="organization.plan"
        data-testid="adminSettings-select-plan"
        dense
        outlined
        required
        item-text="name"
        class="mb-2"
        :rules="[
          plan => !!plan || $t('validation.form.this_field_is_required'),
        ]"
        :label="`${$t('organization.plan')}*`"
        :items="organizationPlans"
      ></v-select>

      <v-select
        v-model="organization.authenticationType"
        data-testid="adminSettings-select-authentication_type"
        dense
        item-text="name"
        outlined
        required
        :rules="[
          authenticationType =>
            !!authenticationType ||
            $t('validation.form.this_field_is_required'),
        ]"
        :label="`${$t('organization.authentication_type')}*`"
        :items="authenticationTypes"
      ></v-select>

      <v-text-field
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlLoginURL"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--loginURL"
        dense
        outlined
        :required="organization.authenticationType === 'saml'"
        :label="`${$t('organization.saml.loginURL')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-text-field>

      <v-text-field
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlEntityID"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--entityID"
        dense
        outlined
        :required="organization.authenticationType === 'saml'"
        :label="`${$t('organization.saml.entityID')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-text-field>

      <v-text-field
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlNameIDField"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--nameIDField"
        dense
        outlined
        :required="organization.authenticationType === 'saml'"
        :label="`${$t('organization.saml.nameIDField')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-text-field>

      <v-textarea
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlX509SigningCertificate"
        auto-grow
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--x509SigningCertificate"
        dense
        outlined
        :required="organization.authenticationType === 'saml'"
        :label="`${$t('organization.saml.x509SigningCertificate')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-textarea>

      <v-textarea
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlFieldMappings"
        auto-grow
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--fieldmappings"
        dense
        outlined
        :required="organization.authenticationType === 'saml'"
        :label="`${$t('organization.saml.fieldMappings')}*`"
        :rules="[url => !!url || $t('validation.form.this_field_is_required')]"
      >
      </v-textarea>

      <v-textarea
        v-if="organization.authenticationType === 'saml'"
        v-model="organization.samlGroupMappings"
        auto-grow
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-saml--groupmappings"
        dense
        outlined
        persistent-placeholder
        :placeholder="`${$t('organization.saml.groupMappings_explanation')}`"
        :label="`${$t('organization.saml.groupMappings')}`"
      >
      </v-textarea>

      <v-text-field
        v-if="hasAzureAuth"
        v-model="organization.providers__azure_authorityURL"
        class="mb-2"
        data-private
        data-testid="adminSettings-textField-azure--authorityURL"
        dense
        outlined
        :hint="`${$t('organization.azure.authority_url_hint')}`"
        :label="`${$t('organization.azure.authority_url')}`"
        :placeholder="`${$t('organization.azure.authority_url_placeholder')}`"
      >
      </v-text-field>

      <v-radio-group
        v-model="organization.isEnabled"
        mandatory
        data-testid="adminSettings-radioGroup-organizationStatus"
      >
        <p class="subtitle1">{{ $t('organization.organization_status') }}*</p>
        <v-radio
          v-for="status in statusOptions"
          :key="status.value"
          :label="status.text"
          :value="status.value"
        ></v-radio>
      </v-radio-group>
      <v-radio-group v-model="organization.isDeleted" mandatory>
        <p class="subtitle1">
          {{ $t('organization.organization_deleted_status') }}*
        </p>
        <v-radio
          v-for="status in deletedOptions"
          :key="status.value"
          :label="status.text"
          :value="status.value"
        ></v-radio>
      </v-radio-group>
      <v-radio-group v-model="organization.hasIntercomDisabled" mandatory>
        <p class="subtitle1">{{ $t('organization.has_intercom_disabled') }}*</p>
        <v-radio
          v-for="status in intercomOptions"
          :key="status.value"
          :label="status.text"
          :value="status.value"
        ></v-radio>
      </v-radio-group>

      <p class="subtitle1">{{ $t('organization.features_enabled') }}</p>
      <v-switch
        v-model="organization.featuresEnabled__quickAction"
        class="pa-0 switch"
        hide-details
        :label="$t('common.quick_action_one')"
      />
      <v-switch
        v-model="organization.featuresEnabled__improvementIdea"
        class="pa-0 switch"
        hide-details
        :label="$t('common.improvement_idea_one')"
      />
      <v-switch
        v-model="organization.featuresEnabled__problemSolving"
        class="pa-0 switch"
        hide-details
        :label="$t('common.problem_solving_one')"
      />
      <v-switch
        v-model="organization.featuresEnabled__routines"
        class="pa-0 switch"
        hide-details
        :label="$t('routines.routine_other')"
      />
      <v-switch
        v-model="organization.featuresEnabled__insights"
        class="pa-0 switch"
        hide-details
        :label="$t('insights.insights')"
      />
      <v-switch
        v-model="organization.featuresEnabled__translation"
        class="pa-0 switch"
        hide-details
        :label="$t('common.translation_other')"
      />
      <v-switch
        v-model="organization.featuresEnabled__boards"
        class="pa-0 switch"
        hide-details
        :label="$t('boards.board_other')"
      />
      <v-switch
        v-model="organization.featuresEnabled__tasks"
        class="pa-0 switch"
        hide-details
        :label="$t('tasks.task_other')"
      />
      <v-switch
        v-model="organization.featuresEnabled__msTeams"
        class="pa-0 switch"
        hide-details
        :label="$t('common.microsoft_teams')"
      />
      <v-switch
        v-model="organization.featuresEnabled__aiFeatures"
        class="pa-0 switch"
        hide-details
        :label="$t('ai_features.ai_features_title')"
      />
    </v-form>

    <div class="mt-6 text-right">
      <Button
        size="large"
        color="grey"
        data-testid="adminSettings-button-reset"
        class="me-2"
        outlined
        :disabled="isLoading || !organizationChanged"
        :text="$t('common.button.discard_changes')"
        @click="$emit('reset')"
      />
      <Button
        size="large"
        color="primary"
        data-testid="adminSettings-button-save"
        :loading="isLoading"
        :disabled="isLoading || !formOptions.valid"
        :text="$t('common.button.save')"
        @click="$emit('update')"
      />
    </div>
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import OrganizationMenu from '@/components/Admin/Organizations/OrganizationMenu.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'AdminSettings',

  mixins: [mixin],

  components: {
    OrganizationMenu,
    Button,
  },

  props: {
    organization: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    organizationChanged: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      formOptions: {
        valid: true,
      },
      language: this.$store.getters.language,
      maxLicenses: this.$constants.organization.maxLicenses,
      maxSitesAvailable: this.$constants.organization.maxSitesAvailable,
      organizationTypes: this.$constants.organization.organizationTypes,
      mediaProviders: this.$constants.organization.mediaProviders,
      organizationPlans: this.$constants.organization.organizationPlans,
      authenticationTypes: this.$constants.organization.authenticationTypes.map(
        item => {
          return {
            value: item.value,
            name: this.$t(item.name),
          }
        }
      ),
      statusOptions: [
        { text: this.$t('organization.enabled'), value: true },
        { text: this.$t('organization.disabled'), value: false },
      ],
      deletedOptions: [
        { text: this.$t('organization.active'), value: false },
        { text: this.$t('organization.deleted'), value: true },
      ],
      intercomOptions: [
        { text: this.$t('common.yes'), value: true },
        { text: this.$t('common.no'), value: false },
      ],
    }
  },

  computed: {
    hasAzureAuth() {
      return this.organization?.authenticationType?.includes('azure')
    },
  },
}
</script>

<style lang="scss" scoped>
.switch {
  width: fit-content;
}
</style>
